import { TypedDocumentNode, gql } from '@apollo/client';
import { isPast } from 'date-fns';

import { AmountWithConversion } from '@sorare/core/src/components/buyActions/AmountWithConversion';
import { SorareUser } from '@sorare/core/src/components/user/SorareUser';
import { monetaryAmountFragment } from '@sorare/core/src/lib/monetaryAmount';

import { ItemSpecialRewardBadge } from 'components/ItemPreview/ItemSpecialRewardBadge';
import {
  ButtonContainer,
  TokenDetailsRoot,
  TokenDetailsRow,
} from 'components/ItemPreview/ui';
import { AuctionStatus } from 'components/auction/AuctionStatus';
import { AuctionsActions } from 'components/auction/AuctionsActions';
import { useTokenTakesPartPromotionalEvent } from 'hooks/offers/useTokenTakesPartPromotionalEvent';
import {
  auctionCurrentPrice,
  promotionalEventsExcludeSpecialRewardBadge,
} from 'lib/auctions';

import { AuctionWinner } from './AuctionWinner';
import { AuctionDetails_auction } from './__generated__/index.graphql';

type Props = {
  auction: AuctionDetails_auction;
  isDesktopLayout: boolean;
  hideSorareUser?: boolean;
  useConversionRate?: boolean;
};

export const AuctionDetails = ({
  auction,
  isDesktopLayout,
  hideSorareUser,
  useConversionRate = false,
}: Props) => {
  const takesPartInEvent = useTokenTakesPartPromotionalEvent();

  const currentPriceMonetary = auctionCurrentPrice(auction);
  const { endDate, cards, bestBid } = auction;
  const hasEnded = isPast(endDate);
  const promotionalEvent = takesPartInEvent(cards);

  return (
    <TokenDetailsRoot>
      <div>
        <TokenDetailsRow>
          <AmountWithConversion
            monetaryAmount={
              !useConversionRate && hasEnded && bestBid
                ? bestBid.amounts
                : currentPriceMonetary
            }
          />
        </TokenDetailsRow>
        {promotionalEvent &&
          !promotionalEventsExcludeSpecialRewardBadge.includes(
            promotionalEvent.name
          ) && <ItemSpecialRewardBadge event={promotionalEvent} />}
        <TokenDetailsRow>
          {hasEnded ? (
            <AuctionWinner auction={auction} />
          ) : (
            <AuctionStatus auction={auction} />
          )}
        </TokenDetailsRow>
      </div>
      {isDesktopLayout && !hideSorareUser && (
        <TokenDetailsRow>
          <SorareUser />
        </TokenDetailsRow>
      )}
      {!hasEnded && (
        <ButtonContainer $isDesktopLayout={isDesktopLayout}>
          <AuctionsActions auction={auction} />
        </ButtonContainer>
      )}
    </TokenDetailsRoot>
  );
};

AuctionDetails.fragments = {
  auction: gql`
    fragment AuctionDetails_auction on TokenAuction {
      id
      open
      bestBid {
        id
        amounts {
          ...MonetaryAmountFragment_monetaryAmount
        }
      }
      cards: anyCards {
        slug
        assetId
        sport
      }
      ...AuctionStatus_auction
      ...auctionCurrentPrice_auction
      ...AuctionWinner_auction
      ...auctionCurrentPrice_auction
      ...AuctionsActions_auction
    }
    ${monetaryAmountFragment}
    ${auctionCurrentPrice.fragments.auction}
    ${AuctionStatus.fragments.auction}
    ${auctionCurrentPrice.fragments.auction}
    ${AuctionWinner.fragments.auction}
    ${AuctionsActions.fragments.auction}
  ` as TypedDocumentNode<AuctionDetails_auction>,
};
